/** The accepted local release only lends its clock to the first salvo. */
export interface FirstVolleyClock { authorityElapsedAtAck: number; localElapsedAtAck: number }

/**
 * Samples an already-received trajectory, never predicts one. The local
 * windup stays intact, then any lead is consumed continuously over the known
 * flight so the final sample/explosion still occurs at the authority impact.
 * Later salvos pass no clock and therefore keep their authority timeline.
 */
export function projectilePresentationElapsed(authorityElapsed: number, launchAt: number, impactAt: number, clock?: FirstVolleyClock): number {
  if (!clock || authorityElapsed >= impactAt || impactAt <= launchAt) return authorityElapsed;
  const lead = clock.localElapsedAtAck - clock.authorityElapsedAtAck;
  if (!(lead > 0)) return authorityElapsed;
  const joinAt = Math.max(clock.authorityElapsedAtAck, launchAt - lead);
  if (joinAt >= impactAt) return authorityElapsed;
  if (authorityElapsed < joinAt) return authorityElapsed + lead;
  // A very late ACK can arrive after a short local flight would have ended.
  // Retain a nonzero piece of the real path; its collision endpoint remains
  // unavailable for visual sampling until the authority impact itself.
  const remainingFlight = Math.min(40, (impactAt - launchAt) / 2);
  const joinSample = Math.min(joinAt + lead, impactAt - remainingFlight);
  const progress = Math.max(0, Math.min(1, (authorityElapsed - joinAt) / (impactAt - joinAt)));
  return joinSample + (impactAt - joinSample) * progress;
}
